import { toast } from '@Backlot-Cars/archie';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { getUserErrorMessageFromResponse } from 'helpers/error';
import DealershipServices from 'services/dealership/dealership.service';
import DocumentsServices from 'services/documents/documents.service';
import PaymentMethodsServices from 'services/paymentMethods/paymentMethods.service';
import UserService from 'services/user/user.service';
import { userIdSelector } from 'store/slices/loggedUsers/selector';
import { flatResponse } from './helpers';

export const getDealershipInfo = createAsyncThunk('getDealershipInfo', async ({ dealershipId }) => {
	const { data } = await DealershipServices.instance.getDealershipForSettings(dealershipId);
	if (data) return data;

	throw new Error('Dealership information could not be fetched');
});

export const getDealershipAddresses = createAsyncThunk(
	'getDealershipAddresses',
	async ({ dealershipId, types, page }) => {
		const { data } = await DealershipServices.instance.getDealershipAddresses(dealershipId, types, page);

		if (data) {
			return {
				data: data.data,
				totalPages: data.pages,
				currentPage: Number(data.current_page),
			};
		}

		throw new Error('Dealership addresses could not be fetched');
	},
);

export const createDealershipAddress = createAsyncThunk(
	'createDealershipAddress',
	async ({ dealershipId, addressData }, { rejectWithValue }) => {
		const genericErrorMessage = 'Dealership address could not be created';

		try {
			const { data } = await DealershipServices.instance.createAddress(dealershipId, { address: addressData });

			if (data) return data;
			return rejectWithValue({ errorMessage: genericErrorMessage });
		} catch (error) {
			const errorMessage = getUserErrorMessageFromResponse(error, genericErrorMessage);
			return rejectWithValue({ errorMessage });
		}
	},
);

export const deleteDealershipAddress = createAsyncThunk(
	'deleteDealershipAddress',
	async ({ dealershipId, addressId }) => {
		try {
			await DealershipServices.instance.deleteAddress(dealershipId, addressId);
			toast.success('Dealership address deleted successfully');
			return { addressId };
		} catch (ex) {
			const errorMessage = ex?.error?.message || 'Dealership address could not be deleted';
			toast.error(errorMessage);
			throw new Error(errorMessage);
		}
	},
);

export const getSettingsNotifications = createAsyncThunk('getSettingsNotifications', async (_, { getState }) => {
	const state = getState();
	const userId = userIdSelector(state);

	const { data } = await UserService.instance.getNotificationPreferences(userId);
	if (data) {
		return flatResponse(data);
	}

	throw new Error('Notification preferences could not be fetched');
});

export const updateSettingsNotifications = createAsyncThunk(
	'updateSettingsNotifications',
	async (notificationData, { getState }) => {
		const state = getState();
		const userId = userIdSelector(state);

		try {
			const { data } = await UserService.instance.updateNotificationPreferences(userId, notificationData);
			return flatResponse(data);
		} catch (ex) {
			const errorMessage = ex?.error?.message || 'Notification preferences could not be updated';
			toast.error(errorMessage);
			throw new Error(errorMessage);
		}
	},
);

export const getSettingsDocuments = createAsyncThunk('getSettingsDocuments', async () => {
	const { data } = await DocumentsServices.instance.fetchDocuments();
	return data;
});

export const getSettingsPaymentMethods = createAsyncThunk('getSettingsPayments', async () => {
	const { data } = await PaymentMethodsServices.instance.getSettingsPaymentMethod();
	return data;
});

export const updateSettingsDefaultPaymentMethod = createAsyncThunk(
	'updateSettingsDefaultPaymentMethod',
	async (submitPayload) => {
		try {
			const { data } = await PaymentMethodsServices.instance.updateSettingsDefaultPaymentMethod(submitPayload);
			return data;
		} catch (ex) {
			const errorMessage =
				ex?.error?.message || 'Default payment method could not be updated, contact support if it continues';
			throw new Error(errorMessage);
		}
	},
);

export const addSettingsFloorPlan = createAsyncThunk(
	'addSettingsFloorPlan',
	async ({ floorPlanCompanyId, accountNumber }) => {
		try {
			const { data } = await PaymentMethodsServices.instance.addSettingsFloorPlan({
				floorPlanCompanyId,
				accountNumber,
			});
			return data;
		} catch (ex) {
			const errorMessage =
				ex?.error?.message || 'Floorplan could not be added, contact support if it continue happening';
			throw new Error(errorMessage);
		}
	},
);
